/* Root component: providers + router (HashRouter so the built app works from file paths/static hosting). */
import { useEffect, useMemo, type ReactNode } from "react";
import { HashRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { QueryClientProvider, type QueryClient } from "@tanstack/react-query";
import { createProvider, DataProviderRoot, type DataProvider } from "../data";
import { createQueryClient } from "../data/hooks";
import { I18nProvider } from "../i18n";
import { ToastProvider, Skeleton } from "../ui";
import { CountryProvider } from "./CountryProvider";
import { BILLING_COUNTRY, CountryScope } from "../lib/country";
import { SessionProvider, useSession, homeFor } from "./session";
import { OverlayProvider } from "./overlays";
import Shell from "./Shell";
import type { Role, User } from "../data/types";
import { onStaticPage, routeForPath } from "../seo/paths";

import Dashboard from "../pages/core/Dashboard";
import Approvals from "../pages/core/Approvals";
import Employees from "../pages/core/Employees";
import EmployeeProfile from "../pages/core/EmployeeProfile";
import EmployeeForm from "../pages/core/EmployeeForm";
import Org from "../pages/core/Org";
import Documents from "../pages/core/Documents";
import Gov from "../pages/core/Gov";
import GovIntegrations from "../pages/core/GovIntegrations";
import Saudization from "../pages/core/Saudization";
import Payroll from "../pages/pay/Payroll";
import PayrollRun from "../pages/pay/PayrollRun";
import Payslip from "../pages/pay/Payslip";
import Expenses from "../pages/pay/Expenses";
import Loans from "../pages/pay/Loans";
import Accounting from "../pages/pay/Accounting";
import Reports from "../pages/pay/Reports";
import Letters from "../pages/pay/Letters";
import Assets from "../pages/pay/Assets";
import Lifecycle from "../pages/pay/Lifecycle";
import Recruit from "../pages/pay/Recruit";
import Performance from "../pages/pay/Performance";
import Cases from "../pages/pay/Cases";
import Audit from "../pages/pay/Audit";
import Settings from "../pages/pay/Settings";
import Leave from "../pages/time/Leave";
import Attendance from "../pages/time/Attendance";
import Biometric from "../pages/time/Biometric";
import DeviceDetail from "../pages/time/DeviceDetail";
import Unmatched from "../pages/time/Unmatched";
import Punches from "../pages/time/Punches";
import Home from "../pages/public/Home";
import Login from "../pages/public/Login";
import Setup from "../pages/public/Setup";
import Pricing from "../pages/public/Pricing";
import Video from "../pages/public/Video";
import Industries from "../pages/public/industries/Industries";
import IndustryMarketing from "../pages/public/industries/IndustryMarketing";
import VerifyEmail from "../pages/public/VerifyEmail";
import ForgotPassword from "../pages/public/ForgotPassword";
import ResetPassword from "../pages/public/ResetPassword";
import DevMailbox from "../pages/public/components/DevMailbox";
import Billing from "../pages/billing/Billing";
import AdminOverview from "../pages/admin/AdminOverview";
import AdminPayments from "../pages/admin/AdminPayments";
import AdminTenants from "../pages/admin/AdminTenants";
import AdminAi from "../pages/admin/AdminAi";
import AdminSettings from "../pages/admin/AdminSettings";
import AdminStorage from "../pages/admin/AdminStorage";
import AdminEmail from "../pages/admin/AdminEmail";
import AiPage from "../pages/ai/AiPage";
import EssHome from "../pages/ess/EssHome";
import EssPayslips from "../pages/ess/EssPayslips";
import EssLeave from "../pages/ess/EssLeave";
import EssAttendance from "../pages/ess/EssAttendance";
import EssDocs from "../pages/ess/EssDocs";
import EssLetters from "../pages/ess/EssLetters";
import EssProfile from "../pages/ess/EssProfile";
import PrintCenter from "../pages/print/PrintCenter";
import IndustryPage from "../pages/industry/Industry";
import Sites from "../pages/industry/Sites";
import { PREFIXED } from "../pages/public/mktCountry";

/** StartHR bills every company in Saudi riyals: the billing page keeps Saudi wording and SAR whatever the company country. */
const SaudiBilling = () => <CountryScope country={BILLING_COUNTRY}><Billing /></CountryScope>;

const T: Role[] = ["owner", "hr", "accountant"];
const OH: Role[] = ["owner", "hr"];
const E: Role[] = ["employee"];
const A: Role[] = ["admin"];

/** Route table: [path, element, roles]. Exported for tests (route coverage). */
export const ROUTES: [string, ReactNode, Role[]][] = [
  ["/dashboard", <Dashboard />, T],
  ["/approvals", <Approvals />, T],
  ["/industry", <IndustryPage />, T],
  ["/sites", <Sites />, T],
  ["/employees", <Employees />, T],
  ["/employees/new", <EmployeeForm />, OH],
  ["/employees/:id/edit", <EmployeeForm />, OH],
  ["/employees/:id", <EmployeeProfile />, T],
  ["/org", <Org />, OH],
  ["/documents", <Documents />, OH],
  ["/gov", <Gov />, OH],
  ["/gov/integrations", <GovIntegrations />, OH],
  ["/saudization", <Saudization />, OH],
  ["/payroll", <Payroll />, T],
  ["/payroll/run", <PayrollRun />, T],
  ["/payslip/:empId/:period", <Payslip />, [...T, "employee"]],
  ["/expenses", <Expenses />, T],
  ["/loans", <Loans />, T],
  ["/accounting", <Accounting />, ["owner", "accountant"]],
  ["/reports", <Reports />, T],
  ["/letters", <Letters />, OH],
  ["/assets", <Assets />, OH],
  ["/lifecycle", <Lifecycle />, OH],
  ["/recruit", <Recruit />, OH],
  ["/performance", <Performance />, OH],
  ["/cases", <Cases />, OH],
  ["/audit", <Audit />, ["owner"]],
  ["/settings", <Settings />, T],
  ["/leave", <Leave />, OH],
  ["/attendance", <Attendance />, OH],
  ["/biometric", <Biometric />, OH],
  ["/biometric/devices/:id", <DeviceDetail />, OH],
  ["/biometric/unmatched", <Unmatched />, OH],
  ["/biometric/punches", <Punches />, OH],
  ["/billing", <SaudiBilling />, ["owner"]],
  ["/ai", <AiPage />, T],
  ["/print", <PrintCenter />, [...T, "employee"]],
  ["/ess", <EssHome />, E],
  ["/ess/payslips", <EssPayslips />, E],
  ["/ess/leave", <EssLeave />, E],
  ["/ess/attendance", <EssAttendance />, E],
  ["/ess/docs", <EssDocs />, E],
  ["/ess/letters", <EssLetters />, E],
  ["/ess/profile", <EssProfile />, E],
  ["/admin", <AdminOverview />, A],
  ["/admin/payments", <AdminPayments />, A],
  ["/admin/tenants", <AdminTenants />, A],
  ["/admin/ai", <AdminAi />, A],
  ["/admin/settings", <AdminSettings />, A],
  ["/admin/email", <AdminEmail />, A],
  ["/admin/storage", <AdminStorage />, A],
];

/** Marketing pages of the other GCC countries: "/ae" (home), "/ae/industries", "/ae/industries/:key" for each
 * prefixed country (enumerated, so no catch-all captures app routes). Keyed by country so a page's state (calculator,
 * filters) resets when the visitor switches country. Saudi Arabia keeps "/home" and "/industries". */
export const COUNTRY_MKT_ROUTES: [string, ReactNode][] = PREFIXED.flatMap((c): [string, ReactNode][] => [
  ["/" + c.slug, <Home key={c.slug} />],
  [`/${c.slug}/industries`, <Industries key={c.slug} />],
  [`/${c.slug}/industries/:key`, <IndustryMarketing key={c.slug} />],
]);

function Guard({ roles, children }: { roles: Role[]; children: ReactNode }) {
  const { user, ready, role } = useSession();
  const loc = useLocation();
  if (!ready) return <div className="content"><Skeleton /></div>;
  if (!user) return <Navigate to={"/login?next=" + encodeURIComponent(loc.pathname + loc.search)} replace />;
  if (!role || !roles.includes(role)) return <Navigate to={homeFor(role)} replace />;
  return <Shell>{children}</Shell>;
}

function RootRedirect() {
  const { user, ready, role } = useSession();
  if (!ready) return null;
  return <Navigate to={user ? homeFor(role) : "/home"} replace />;
}

/** Account pages show the mock-mode developer mailbox (renders nothing against the real API). */
const WithMailbox = ({ children }: { children: ReactNode }) => <>{children}<DevMailbox /></>;

/** Prerendered marketing page this document was opened at ("/industries/garage/"), if any (see src/seo/paths.ts). */
export const staticEntry = () => (typeof window !== "undefined" && onStaticPage() ? routeForPath(window.location.pathname) : null);

export function AppRoutes() {
  const loc = useLocation();
  // On a prerendered page the hash is empty: show that page's route instead of redirecting to /home.
  const entry = loc.pathname === "/" ? staticEntry() : null;
  // ...and app routes reached from it (Sign in, Start free trial) continue in the app document at "/".
  const leave = loc.pathname !== "/" && staticEntry() !== null;
  useEffect(() => { if (leave) window.location.replace("/#" + loc.pathname + loc.search); }, [leave, loc.pathname, loc.search]);
  return (
    <Routes location={entry ? entry.route + window.location.search : undefined}>
      <Route path="/" element={<RootRedirect />} />
      <Route path="/home" element={<Home />} />
      <Route path="/login" element={<WithMailbox><Login /></WithMailbox>} />
      <Route path="/setup" element={<WithMailbox><Setup /></WithMailbox>} />
      <Route path="/verify-email" element={<WithMailbox><VerifyEmail /></WithMailbox>} />
      <Route path="/forgot-password" element={<WithMailbox><ForgotPassword /></WithMailbox>} />
      <Route path="/reset-password" element={<WithMailbox><ResetPassword /></WithMailbox>} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/video" element={<Video />} />
      <Route path="/industries" element={<Industries />} />
      <Route path="/industries/:key" element={<IndustryMarketing />} />
      {COUNTRY_MKT_ROUTES.map(([p, el]) => <Route key={p} path={p} element={el} />)}
      {ROUTES.map(([p, el, roles]) => <Route key={p} path={p} element={<Guard roles={roles}>{el}</Guard>} />)}
      <Route path="*" element={<RootRedirect />} />
    </Routes>
  );
}

export function AppProviders({ provider, children, lang, initialUser, queryClient }: {
  provider: DataProvider; children: ReactNode; lang?: "en" | "ar";
  /** Known session user (skips the async me() probe); omit to validate the provider's token. */
  initialUser?: User | null;
  /** Pre-built (optionally pre-filled) query client; default: a fresh one per provider. */
  queryClient?: QueryClient;
}) {
  const own = useMemo(() => createQueryClient(provider.mode), [provider]);
  const qc = queryClient ?? own;
  return (
    <QueryClientProvider client={qc}>
      <DataProviderRoot provider={provider}>
        <I18nProvider initialLang={lang}>
          <ToastProvider>
            <SessionProvider initialUser={initialUser}><CountryProvider>{children}</CountryProvider></SessionProvider>
          </ToastProvider>
        </I18nProvider>
      </DataProviderRoot>
    </QueryClientProvider>
  );
}

export default function App({ provider }: { provider?: DataProvider }) {
  const p = useMemo(() => provider ?? createProvider(), [provider]);
  const entry = useMemo(staticEntry, []);
  return (
    <AppProviders provider={p} lang={entry?.lang}>
      <HashRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <OverlayProvider>
          <AppRoutes />
        </OverlayProvider>
      </HashRouter>
    </AppProviders>
  );
}
